/** Restaura a sessão a partir do refresh token guardado.
 *
 * Sem isto, TODO reload manda o aluno de volta para o login — o access token
 * vive só em memória. Para um leitor que o aluno usa a aula inteira, isso é
 * inaceitável na prática, e nenhum teste unitário pega: só se vê no navegador.
 *
 * Estados: `verificando` (nada na tela ainda), `autenticado`, `anonimo`.
 */
import { useEffect, useState } from 'react'
import axios from 'axios'

import { refreshOnce } from '@/services/api'
import { me } from '@/services/authService'
import { useAuthStore } from '@/stores/authStore'

export type SessionStatus = 'verificando' | 'autenticado' | 'anonimo' | 'indisponivel'

export function useSessionBootstrap(): SessionStatus {
  const [status, setStatus] = useState<SessionStatus>('verificando')

  useEffect(() => {
    let cancelado = false
    let running = false

    async function restaurar() {
      if (running || cancelado) return
      running = true
      try {
        const { accessToken, refreshToken } = useAuthStore.getState()
        if (accessToken && useAuthStore.getState().user) {
          if (!cancelado) setStatus('autenticado')
          return
        }
        if (!refreshToken) {
          if (!cancelado) setStatus('anonimo')
          return
        }
        const novo = accessToken || await refreshOnce()
        if (!novo) {
          if (!cancelado) setStatus('anonimo')
          return
        }
        await me()
        if (!cancelado) setStatus('autenticado')
      } catch (error) {
        if (axios.isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
          useAuthStore.getState().clear()
          if (!cancelado) setStatus('anonimo')
        } else if (!cancelado) setStatus('indisponivel')
      } finally {
        running = false
      }
    }

    void restaurar()
    const retry = () => { void restaurar() }
    window.addEventListener('online', retry)
    window.addEventListener('lumina:retry-session', retry)
    return () => {
      cancelado = true
      window.removeEventListener('online', retry)
      window.removeEventListener('lumina:retry-session', retry)
    }
  }, [])

  return status
}
